import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import {
  ArrowRight,
  Award,
  Bell,
  BookOpen,
  Brain,
  Calendar,
  Check,
  CheckCircle,
  Clock,
  FileCheck,
  FileText,
  Filter,
  Languages,
  Mail,
  MapPin,
  MessageCircle,
  Play,
  Search,
  Settings,
  Shield,
  Smartphone,
  User,
  Users,
  X,
  Zap,
} from "lucide-react";
import React from "react";
import Footer from "../Footer";
import Header from "../Header";
import MiniFooter from "../MiniFooter";

const searchFeatures = [
  {
    icon: Search,
    title: "Recherche sémantique",
    description:
      "Trouvez des documents par sens et contexte, pas seulement par mots-clés exacts",
  },
  {
    icon: Filter,
    title: "Filtres avancés",
    description:
      "Affinez vos résultats par type de document, juridiction, date et bien plus",
  },
  {
    icon: Calendar,
    title: "Recherche temporelle",
    description:
      "Explorez l'évolution jurisprudentielle dans le temps avec nos filtres chronologiques",
  },
  {
    icon: MapPin,
    title: "Géolocalisation juridique",
    description:
      "Filtrez par juridiction, tribunal ou zone géographique d'application",
  },
  {
    icon: User,
    title: "Recherche par auteur",
    description:
      "Trouvez rapidement les décisions d'un juge ou les écrits d'un juriste spécifique",
  },
  {
    icon: FileText,
    title: "Types de documents",
    description:
      "Jurisprudence, doctrine, textes législatifs, contrats types et bien plus",
  },
];

const aiFeatures = [
  {
    icon: Brain,
    title: "Compréhension contextuelle",
    description:
      "Notre IA comprend les nuances du langage juridique et le contexte de vos questions",
  },
  {
    icon: Zap,
    title: "Réponses instantanées",
    description:
      "Obtenez des réponses précises en quelques secondes, 24h/24 et 7j/7",
  },
  {
    icon: Clock,
    title: "Historique des conversations",
    description:
      "Retrouvez facilement vos échanges précédents et continuez vos recherches",
  },
  {
    icon: Languages,
    title: "Langage naturel",
    description:
      "Posez vos questions comme vous le feriez à un collègue juriste",
  },
  {
    icon: BookOpen,
    title: "Sources citées",
    description:
      "Chaque réponse est accompagnée de ses sources juridiques vérifiables",
  },
  {
    icon: MessageCircle,
    title: "Conversation continue",
    description:
      "Approfondissez vos questions dans une conversation fluide et naturelle",
  },
];

const certificationFeatures = [
  {
    icon: Shield,
    title: "Validation par experts",
    description:
      "Chaque réponse peut être certifiée par nos juristes spécialisés",
  },
  {
    icon: CheckCircle,
    title: "Garantie de fiabilité",
    description:
      "Obtenez une assurance juridique sur la précision des informations",
  },
  {
    icon: Clock,
    title: "Délais rapides",
    description: "Certification sous 24h pour les demandes urgentes",
  },
  {
    icon: Award,
    title: "Experts qualifiés",
    description: "Notre réseau compte plus de 200 juristes spécialisés",
  },
  {
    icon: Users,
    title: "Spécialisation par domaine",
    description: "Chaque expert intervient dans son domaine de compétence",
  },
  {
    icon: FileCheck,
    title: "Certificat expert",
    description: "Recevez un certificat de validation pour vos dossiers",
  },
];

const certificationProcess = [
  {
    step: "1",
    title: "Demande de certification",
    description: "Sélectionnez la réponse IA à faire certifier",
  },
  {
    step: "2",
    title: "Attribution à un expert",
    description: "Un juriste spécialisé examine votre demande",
  },
  {
    step: "3",
    title: "Validation et retour",
    description: "Recevez la certification avec commentaires éventuels",
  },
];

const alertTypes = [
  {
    icon: Bell,
    title: "Nouvelles jurisprudences",
    description:
      "Soyez alerté des dernières décisions dans vos domaines de spécialité",
    color: "bg-blue-100 text-blue-600",
  },
  {
    icon: Filter,
    title: "Modifications législatives",
    description:
      "Recevez les mises à jour des textes de loi qui vous concernent",
    color: "bg-green-100 text-green-600",
  },
  {
    icon: Zap,
    title: "Alertes urgentes",
    description: "Notifications immédiates pour les changements critiques",
    color: "bg-red-100 text-red-600",
  },
  {
    icon: Settings,
    title: "Critères personnalisés",
    description: "Configurez vos alertes selon vos besoins spécifiques",
    color: "bg-purple-100 text-purple-600",
  },
];

const deliveryMethods = [
  {
    icon: Mail,
    title: "Email",
    description: "Recevez un résumé quotidien ou hebdomadaire par email",
  },
  {
    icon: Smartphone,
    title: "Notifications push",
    description: "Alertes instantanées sur votre mobile ou desktop",
  },
  {
    icon: Bell,
    title: "Dans l'application",
    description: "Centre de notifications intégré à votre dashboard",
  },
];

const comparisonData = [
  {
    feature: "Recherche par mots-clés",
    traditional: true,
    docuveritas: true,
  },
  {
    feature: "Recherche sémantique",
    traditional: false,
    docuveritas: true,
  },
  {
    feature: "Chatbot IA juridique",
    traditional: false,
    docuveritas: true,
  },
  {
    feature: "Certification par experts",
    traditional: false,
    docuveritas: true,
  },
  {
    feature: "Alertes personnalisées",
    traditional: false,
    docuveritas: true,
  },
  {
    feature: "Interface moderne",
    traditional: false,
    docuveritas: true,
  },
  {
    feature: "Support 24/7",
    traditional: false,
    docuveritas: true,
  },
  {
    feature: "Mise à jour en temps réel",
    traditional: false,
    docuveritas: true,
  },
];

const benefits = [
  "Essai gratuit de 14 jours",
  "Accès à toutes les fonctionnalités",
  "Support client dédié",
  "Aucun engagement",
];

export default function Features() {
  return (
    <div className="min-h-screen bg-white">
      <Header />
      <main>
        <section className="bg-gradient-to-br from-blue-50 to-white py-20 lg:py-32">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center max-w-4xl mx-auto">
              <h1 className="text-4xl md:text-6xl font-bold text-gray-900 mb-6">
                Fonctionnalités <span className="text-blue-600">avancées</span>
              </h1>
              <p className="text-xl md:text-2xl text-gray-600 mb-8 leading-relaxed">
                Découvrez en détail les outils qui révolutionnent la recherche
                juridique et optimisent votre productivité quotidienne.
              </p>
              <p className="text-lg text-gray-600 mb-12 leading-relaxed">
                De la recherche sémantique intelligente à la certification par
                des experts, chaque fonctionnalité a été conçue pour répondre
                aux besoins spécifiques des professionnels du droit.
              </p>

              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <Button
                  size="lg"
                  className="bg-blue-600 hover:bg-blue-700 text-white px-8 py-4 text-lg rounded-xl"
                >
                  Tester gratuitement
                  <ArrowRight className="ml-2 h-5 w-5" />
                </Button>
                <Button
                  size="lg"
                  variant="outline"
                  className="border-2 border-slate-500 text-slate-500 hover:bg-slate-50 px-8 py-4 text-lg rounded-xl bg-transparent"
                >
                  <Play className="mr-2 h-5 w-5" />
                  Voir la démo
                </Button>
              </div>
            </div>
          </div>
        </section>

        <section className="py-20 bg-white">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
              {/* Content */}
              <div>
                <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-6">
                  Base documentaire intelligente
                </h2>
                <p className="text-xl text-gray-600 mb-8 leading-relaxed">
                  Accédez à la plus complète base de données juridiques
                  française avec des outils de recherche révolutionnaires qui
                  comprennent le contexte et le sens de vos requêtes.
                </p>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  {searchFeatures.map((feature, index) => (
                    <div key={index} className="flex items-start space-x-4">
                      <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center flex-shrink-0">
                        <feature.icon className="h-5 w-5 text-blue-600" />
                      </div>
                      <div>
                        <h3 className="font-semibold text-gray-900 mb-2">
                          {feature.title}
                        </h3>
                        <p className="text-sm text-gray-600 leading-relaxed">
                          {feature.description}
                        </p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Mockup */}
              <div className="relative">
                <Card className="border-2 border-gray-200 rounded-2xl overflow-hidden shadow-2xl">
                  <CardContent className="p-0">
                    <div className="bg-gray-50 px-6 py-4 border-b border-gray-200">
                      <div className="flex items-center space-x-2">
                        <div className="w-3 h-3 bg-red-400 rounded-full"></div>
                        <div className="w-3 h-3 bg-yellow-400 rounded-full"></div>
                        <div className="w-3 h-3 bg-green-400 rounded-full"></div>
                      </div>
                    </div>
                    <div>
                      <img
                        src="/Capture chatbot.png"
                        alt="Interface de recherche documentaire"
                        className="w-full h-auto"
                      />
                    </div>
                  </CardContent>
                </Card>
              </div>
            </div>
          </div>
        </section>

        <section className="py-20 bg-gray-50">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
              {/* Mockup */}
              <div className="relative order-2 lg:order-1">
                <Card className="border-2 border-gray-200 rounded-2xl overflow-hidden shadow-2xl">
                  <CardContent className="p-0">
                    <div className="bg-slate-500 px-6 py-4 text-white">
                      <div className="flex items-center space-x-3">
                        <MessageCircle className="h-6 w-6" />
                        <h3 className="font-semibold">
                          Assistant IA Juridique
                        </h3>
                        <div className="ml-auto w-2 h-2 bg-green-400 rounded-full"></div>
                      </div>
                    </div>
                    <div className="bg-white">
                      <img
                        src="/Capture chatbot.png"
                        alt="Interface chatbot IA juridique"
                        className="w-full h-auto"
                      />
                    </div>
                  </CardContent>
                </Card>
              </div>

              {/* Content */}
              <div className="order-1 lg:order-2">
                <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-6">
                  Chatbot IA juridique avancé
                </h2>
                <p className="text-xl text-gray-600 mb-8 leading-relaxed">
                  Posez vos questions juridiques en langage naturel et obtenez
                  des réponses précises, contextualisées et sourcées par notre
                  intelligence artificielle spécialisée.
                </p>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  {aiFeatures.map((feature, index) => (
                    <div key={index} className="flex items-start space-x-4">
                      <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center flex-shrink-0">
                        <feature.icon className="h-5 w-5 text-blue-600" />
                      </div>
                      <div>
                        <h3 className="font-semibold text-gray-900 mb-2">
                          {feature.title}
                        </h3>
                        <p className="text-sm text-gray-600 leading-relaxed">
                          {feature.description}
                        </p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </section>

        <section className="py-20 bg-white">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-16">
              <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-6">
                Certification par des experts
              </h2>
              <p className="text-xl text-gray-600 max-w-3xl mx-auto leading-relaxed">
                Transformez les réponses de l'IA en avis juridiques certifiés
                par nos experts pour une sécurité juridique maximale.
              </p>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 mb-16">
              {/* Features */}
              <div>
                <h3 className="text-2xl font-bold text-gray-900 mb-8">
                  Pourquoi certifier vos réponses ?
                </h3>
                <div className="space-y-6">
                  {certificationFeatures.map((feature, index) => (
                    <div key={index} className="flex items-start space-x-4">
                      <div className="w-10 h-10 bg-green-100 rounded-lg flex items-center justify-center flex-shrink-0">
                        <feature.icon className="h-5 w-5 text-green-600" />
                      </div>
                      <div>
                        <h4 className="font-semibold text-gray-900 mb-2">
                          {feature.title}
                        </h4>
                        <p className="text-gray-600 leading-relaxed">
                          {feature.description}
                        </p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Process */}
              <div>
                <h3 className="text-2xl font-bold text-gray-900 mb-8">
                  Comment ça fonctionne ?
                </h3>
                <div className="space-y-8">
                  {certificationProcess.map((process, index) => (
                    <div key={index} className="relative">
                      {index < certificationProcess.length - 1 && (
                        <div className="absolute left-6 top-12 w-0.5 h-16 bg-slate-200"></div>
                      )}
                      <div className="flex items-start space-x-4">
                        <div className="w-12 h-12 bg-slate-500 text-white rounded-full flex items-center justify-center font-bold text-lg flex-shrink-0">
                          {process.step}
                        </div>
                        <div className="pt-2">
                          <h4 className="font-semibold text-gray-900 mb-2">
                            {process.title}
                          </h4>
                          <p className="text-gray-600 leading-relaxed">
                            {process.description}
                          </p>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>

            {/* Mockup */}
            <div className="relative">
              <Card className="border-2 border-gray-200 rounded-2xl overflow-hidden shadow-2xl max-w-4xl mx-auto">
                <CardContent className="p-0">
                  <div className="bg-green-50 px-6 py-4 border-b border-green-200">
                    <div className="flex items-center space-x-3">
                      <Shield className="h-6 w-6 text-green-600" />
                      <h3 className="font-semibold text-green-800">
                        Processus de certification
                      </h3>
                      <div className="ml-auto px-3 py-1 bg-green-200 text-green-800 rounded-full text-sm font-medium">
                        Certifié
                      </div>
                    </div>
                  </div>
                  <div>
                    <img
                      src="/Capture chatbot.png"
                      alt="Interface de certification par expert"
                      className="w-full h-auto"
                    />
                  </div>
                </CardContent>
              </Card>
            </div>
          </div>
        </section>

        <section className="py-20 bg-gray-50">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-16">
              <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-6">
                Alertes personnalisées
              </h2>
              <p className="text-xl text-gray-600 max-w-3xl mx-auto leading-relaxed">
                Restez informé en temps réel des évolutions juridiques qui
                impactent votre pratique grâce à notre système d'alertes
                intelligent.
              </p>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 mb-16">
              {/* Alert Types */}
              <div>
                <h3 className="text-2xl font-bold text-gray-900 mb-8">
                  Types d'alertes disponibles
                </h3>
                <div className="space-y-6">
                  {alertTypes.map((alert, index) => (
                    <Card
                      key={index}
                      className="border-2 hover:border-slate-300 transition-colors rounded-xl"
                    >
                      <CardContent className="p-6">
                        <div className="flex items-start space-x-4">
                          <div
                            className={`w-12 h-12 rounded-lg flex items-center justify-center ${alert.color}`}
                          >
                            <alert.icon className="h-6 w-6" />
                          </div>
                          <div>
                            <h4 className="font-semibold text-gray-900 mb-2">
                              {alert.title}
                            </h4>
                            <p className="text-gray-600 leading-relaxed">
                              {alert.description}
                            </p>
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  ))}
                </div>
              </div>

              {/* Delivery Methods */}
              <div>
                <h3 className="text-2xl font-bold text-gray-900 mb-8">
                  Modes de livraison
                </h3>
                <div className="space-y-6 mb-8">
                  {deliveryMethods.map((method, index) => (
                    <div key={index} className="flex items-start space-x-4">
                      <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center flex-shrink-0">
                        <method.icon className="h-5 w-5 text-blue-600" />
                      </div>
                      <div>
                        <h4 className="font-semibold text-gray-900 mb-2">
                          {method.title}
                        </h4>
                        <p className="text-gray-600 leading-relaxed">
                          {method.description}
                        </p>
                      </div>
                    </div>
                  ))}
                </div>

                {/* Configuration Example */}
                <Card className="border-2 border-slate-200 rounded-xl">
                  <CardContent className="p-6">
                    <h4 className="font-semibold text-gray-900 mb-4">
                      Exemple de configuration
                    </h4>
                    <div className="space-y-3 text-sm">
                      <div className="flex justify-between items-center py-2 border-b border-gray-100">
                        <span className="text-gray-600">
                          Droit des sociétés
                        </span>
                        <span className="px-2 py-1 bg-green-100 text-green-800 rounded-full text-xs">
                          Actif
                        </span>
                      </div>
                      <div className="flex justify-between items-center py-2 border-b border-gray-100">
                        <span className="text-gray-600">
                          Jurisprudence CJUE
                        </span>
                        <span className="px-2 py-1 bg-green-100 text-green-800 rounded-full text-xs">
                          Actif
                        </span>
                      </div>
                      <div className="flex justify-between items-center py-2">
                        <span className="text-gray-600">Droit fiscal</span>
                        <span className="px-2 py-1 bg-gray-100 text-gray-600 rounded-full text-xs">
                          Inactif
                        </span>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </div>
            </div>

            {/* Mockup */}
            <div className="relative">
              <Card className="border-2 border-gray-200 rounded-2xl overflow-hidden shadow-2xl max-w-4xl mx-auto">
                <CardContent className="p-0">
                  <div className="bg-slate-500 px-6 py-4 text-white">
                    <div className="flex items-center space-x-3">
                      <Bell className="h-6 w-6" />
                      <h3 className="font-semibold">
                        Centre d'alertes personnalisées
                      </h3>
                      <div className="ml-auto px-3 py-1 bg-slate-400 rounded-full text-sm">
                        3 nouvelles
                      </div>
                    </div>
                  </div>
                  <div>
                    <img
                      src="/Capture chatbot.png"
                      alt="Interface alertes personnalisées"
                      className="w-full h-auto"
                    />
                  </div>
                </CardContent>
              </Card>
            </div>
          </div>
        </section>

        <section className="py-20 bg-white">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-16">
              <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-6">
                Pourquoi choisir Docuveritas ?
              </h2>
              <p className="text-xl text-gray-600 max-w-3xl mx-auto leading-relaxed">
                Découvrez les avantages concurrentiels qui font de Docuveritas
                la solution de référence pour les professionnels du droit.
              </p>
            </div>

            <div className="max-w-4xl mx-auto">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
                {/* Traditional Solutions */}
                <Card className="border-2 border-gray-200 rounded-xl">
                  <CardHeader className="text-center pb-6">
                    <CardTitle className="text-xl text-gray-700">
                      Concurrence
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="pt-0">
                    <div className="space-y-4">
                      {comparisonData.map((item, index) => (
                        <div
                          key={index}
                          className="flex items-center justify-between py-2"
                        >
                          <span className="text-sm text-gray-600">
                            {item.feature}
                          </span>
                          {item.traditional ? (
                            <Check className="h-5 w-5 text-green-500" />
                          ) : (
                            <X className="h-5 w-5 text-red-400" />
                          )}
                        </div>
                      ))}
                    </div>
                  </CardContent>
                </Card>

                {/* Docuveritas */}
                <Card className="border-2 border-slate-500 rounded-xl shadow-xl scale-105 relative">
                  <div className="absolute -top-4 left-1/2 transform -translate-x-1/2">
                    <span className="bg-slate-500 text-white px-4 py-2 rounded-full text-sm font-medium">
                      Recommandé
                    </span>
                  </div>
                  <CardHeader className="text-center pb-6">
                    <CardTitle className="text-xl text-slate-500 font-bold">
                      Docuveritas
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="pt-0">
                    <div className="space-y-4">
                      {comparisonData.map((item, index) => (
                        <div
                          key={index}
                          className="flex items-center justify-between py-2"
                        >
                          <span className="text-sm text-gray-900 font-medium">
                            {item.feature}
                          </span>
                          {item.docuveritas ? (
                            <Check className="h-5 w-5 text-green-500" />
                          ) : (
                            <X className="h-5 w-5 text-red-400" />
                          )}
                        </div>
                      ))}
                    </div>
                  </CardContent>
                </Card>

                {/* Benefits */}
                <div className="space-y-6">
                  <h3 className="text-xl font-bold text-gray-900">
                    Avantages clés
                  </h3>
                  <div className="space-y-4">
                    <div className="flex items-start space-x-3">
                      <Check className="h-5 w-5 text-green-500 mt-1 flex-shrink-0" />
                      <div>
                        <h4 className="font-semibold text-gray-900 text-sm">
                          Gain de temps
                        </h4>
                        <p className="text-xs text-gray-600">
                          Jusqu'à 70% de temps économisé sur vos recherches
                        </p>
                      </div>
                    </div>
                    <div className="flex items-start space-x-3">
                      <Check className="h-5 w-5 text-green-500 mt-1 flex-shrink-0" />
                      <div>
                        <h4 className="font-semibold text-gray-900 text-sm">
                          Fiabilité garantie
                        </h4>
                        <p className="text-xs text-gray-600">
                          Certification par des experts juridiques
                        </p>
                      </div>
                    </div>
                    <div className="flex items-start space-x-3">
                      <Check className="h-5 w-5 text-green-500 mt-1 flex-shrink-0" />
                      <div>
                        <h4 className="font-semibold text-gray-900 text-sm">
                          Innovation continue
                        </h4>
                        <p className="text-xs text-gray-600">
                          Mises à jour régulières et nouvelles fonctionnalités
                        </p>
                      </div>
                    </div>
                    <div className="flex items-start space-x-3">
                      <Check className="h-5 w-5 text-green-500 mt-1 flex-shrink-0" />
                      <div>
                        <h4 className="font-semibold text-gray-900 text-sm">
                          Support expert
                        </h4>
                        <p className="text-xs text-gray-600">
                          Équipe dédiée de juristes et techniciens
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>
        <MiniFooter />
      </main>
      <Footer />
    </div>
  );
}
